import { BigNumber } from 'ethers'
import { Address, TokenId, TokenSymbol } from '../../types'
import { store } from '../../store'
import {
  addDepositPendingTxAction,
  updateDepositingAction,
} from '../../store/deposit/actions'
import { formatUnits } from 'ethers/lib/utils'
import { numberToHex } from '../../utils/number'
import { useDispatch } from 'react-redux'
import {
  useWeb3Address,
  useWeb3ChainId,
  useWeb3Provider,
} from '../../store/web3/hooks'
import { useCallback } from 'react'
import { updateModal } from '../../store/app/actions/modalActions'
import { Logger } from '@ethersproject/logger'
import toastify from '../../components/Toastify'
import { latestDepositExplorerUrlAtom } from '../../components/WaitingForConfirmationModal/atoms'
import { useSetAtom } from 'jotai'
import { ErrorCode } from '../../connection/utils'
import { sendDeposit } from './deposit'
import { NetworkRegistry } from '../../utils/networkRegistry'
import { useIsActivated } from 'store/account/hooks'
import { TxStatusEnum } from 'store/deposit/types'

export function useDeposit() {
  const dispatch = useDispatch()
  const provider = useWeb3Provider()
  const chainId = useWeb3ChainId()
  const address = useWeb3Address()
  const updateLatestDepositExplorerUrl = useSetAtom(
    latestDepositExplorerUrlAtom,
  )
  const isActivated = useIsActivated()
  return useCallback(
    async function deposit({
      tokenId,
      tokenAddress,
      tokenSymbol,
      amount,
      decimals,
    }: {
      tokenId: TokenId
      tokenAddress: Address
      tokenSymbol: TokenSymbol
      amount: BigNumber
      decimals: number
    }) {
      const state = store.getState()
      try {
        if (!amount || !BigNumber.isBigNumber(amount) || Number(amount) < 0) {
          return
        }
        if (!chainId) {
          throw new Error(`Deposit: Unknown chainId, ${chainId}`)
        }
        const network = NetworkRegistry.byL1(numberToHex(chainId))

        if (!network) {
          throw new Error(`Deposit: Cannot find network, ${chainId}`)
        }
        dispatch(updateDepositingAction(true))

        if (!provider) {
          throw new Error(`Deposit: provider is undefined, ${chainId}`)
        }

        let { layerOneChainId, layerTwoChainId } = network

        const transactionResponse = await sendDeposit(provider!, {
          chainId: layerOneChainId,
          from: address,
          depositTo: address,
          token: tokenAddress,
          amount,
        }).catch((error) => {
          throw error
        })

        updateLatestDepositExplorerUrl(
          `${network.explorerUrl}/tx/${transactionResponse.hash}`,
        )

        dispatch(updateModal({ modal: 'deposit', open: false }))

        // If the account is not activated, show the waiting for confirmation modal
        if (!isActivated) {
          dispatch(updateModal({ modal: 'depositing', open: true }))
        }

        // Add a pending tx to store
        dispatch(
          addDepositPendingTxAction({
            createdAt: Date.now(),
            tokenSymbol,
            amount: formatUnits(amount, decimals),
            ethHash: transactionResponse.hash,
            chainId: layerTwoChainId,
            layerOneChainId,
            fromAddress: transactionResponse.from,
            toAddress: address,
            currencyId: tokenId,
            txStatus: TxStatusEnum.PENDING,
          }),
        )

        // Show the pending txs modal on footer
        dispatch(updateModal({ modal: 'pendingTxs', open: true }))
      } catch (e) {
        const connectorName = state.settings.connectorName
        // User rejected the tx returned by MetaMask
        if (e?.code === Logger.errors.ACTION_REJECTED) {
        }
        // User rejected the tx returned by anther web3 like Rabby
        else if (e?.code === ErrorCode.USER_REJECTED_REQUEST) {
        } else if (
          e?.message.indexOf('replacement fee too low') > -1 ||
          e?.message.indexOf('transaction underpriced') > -1
        ) {
          toastify.error(
            `The auto-estimated gas fee in your wallet is too low, please try again later, or increase the gas fee manually in your wallet`,
          )
        } else if (e?.message.indexOf('cannot estimate gas') > -1) {
          toastify.error(`Transaction may fail or may require manual gas limit`)
        } else if (e?.data?.message) {
          toastify.error(
            `[${connectorName} Error] code: ${e?.code}, ${e?.data?.message}`,
          )
        } else {
          toastify.error(`[${connectorName} Error] ${e?.message}`)
          console.error(e)
        }
      } finally {
        dispatch(updateDepositingAction(false))
      }
    },
    [
      dispatch,
      updateLatestDepositExplorerUrl,
      address,
      chainId,
      provider,
      isActivated,
    ],
  )
}
